/**
 * EnumControl — Radio pills for <=7 options, dropdown for >7.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'

interface EnumControlProps {
  value: string | number
  onChange: (v: string | number) => void
  label: string
  options: (string | number)[]
}

/** Humanize an option value like "products_by_category" → "Products By Category" */
function humanize(val: string | number): string {
  return String(val)
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function EnumControl({ value, onChange, label, options }: EnumControlProps) {
  const usePills = options.length <= 7

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>
      {usePills ? (
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const isSelected = String(value) === String(opt)
            return (
              <button
                key={String(opt)}
                type="button"
                onClick={() => onChange(opt)}
                className={`rounded-xs px-3 py-1.5 text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-sky-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {humanize(opt)}
              </button>
            )
          })}
        </div>
      ) : (
        <Select
          value={String(value)}
          onValueChange={(v) => {
            // Preserve number type if original options are numbers
            const original = options.find((o) => String(o) === v)
            onChange(original ?? v)
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={String(opt)} value={String(opt)}>
                {humanize(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
